/**
 * Subdomain-based configuration system for multi-country medical tourism platform
 *
 * This module detects the current subdomain and provides market-specific configuration.
 *
 * SIMPLIFIED DEPLOYMENT MODEL:
 * - Each country deployment uses a single set of environment variables (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY)
 * - Deploy to different countries by swapping .env files (e.g., .env.korea, .env.vietnam)
 * - Subdomain detection is maintained for market identification and routing
 *
 * Architecture:
 * - medipaths.co (main domain) → Global market (defaults to English)
 * - vn.medipaths.co → Vietnamese market
 * - Extensible for additional countries/subdomains
 *
 * Development environment support:
 * - localhost → Uses environment configuration
 * - localhost:5173?subdomain=vn → Simulates Vietnamese subdomain for market detection
 * - vn.localhost:5173 → Vietnamese subdomain (requires hosts file setup)
 */

/**
 * Market configuration interface
 */
export interface MarketConfig {
  market: string;
  defaultLanguage: string;
}

/**
 * Subdomain configuration interface
 */
export interface SubdomainConfig extends MarketConfig {
  supabaseUrl: string | undefined;
  supabaseAnonKey: string | undefined;
  subdomain: string;
}

/**
 * Market configuration mapping based on subdomain
 * This provides market-specific metadata (language, market identifier)
 * while Supabase credentials come from environment variables
 */
const MARKET_CONFIG: Record<string, MarketConfig> = {
  // Default/English market (medipaths.co) - Main domain defaults to English
  default: {
    market: 'global',
    defaultLanguage: 'en',
  },

  // Vietnam market (vn.medipaths.co)
  vn: {
    market: 'vietnam',
    defaultLanguage: 'vi',
  },

  // Add more subdomains here as needed:
  // th: { market: "thailand", defaultLanguage: "th" }
  // id: { market: "indonesia", defaultLanguage: "id" }
  // etc.
};

/**
 * Extract subdomain from hostname
 * @param hostname - The hostname (e.g., "vn.medipaths.co", "localhost")
 * @returns Subdomain or null if no subdomain
 */
function extractSubdomain(hostname: string): string | null {
  // Handle localhost and IP addresses
  if (
    hostname === 'localhost' ||
    hostname.startsWith('127.0.0.1') ||
    hostname.startsWith('192.168.')
  ) {
    return null;
  }

  // Handle subdomains in localhost format (e.g., vn.localhost)
  if (hostname.includes('.localhost')) {
    const parts = hostname.split('.');
    return parts.length > 0 ? (parts[0] as string) : null;
  }

  // Split hostname into parts
  const parts = hostname.split('.');

  // If only one or two parts, no subdomain (e.g., "medipaths.co" or "localhost")
  if (parts.length <= 2) {
    return null;
  }

  // Handle www subdomain - treat as no subdomain
  if (parts[0] === 'www') {
    return null;
  }

  // Return first part as subdomain (e.g., "vn" from "vn.medipaths.co")
  return parts.length > 0 ? (parts[0] as string) : null;
}

/**
 * Get subdomain from URL search parameters (for development testing)
 * @returns Subdomain from URL parameter or null
 */
function getMarketConfig(subdomain: string | null): MarketConfig {
  switch (subdomain) {
    case 'vn':
      return MARKET_CONFIG.vn as MarketConfig;
    case 'default':
      return MARKET_CONFIG.default as MarketConfig;
    default:
      return MARKET_CONFIG.default as MarketConfig;
  }
}

function getSubdomainFromURLParam(): string | null {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('subdomain');
}

/**
 * Detect current subdomain with multiple fallback strategies
 * @returns {string|null} Detected subdomain or null for default
 */
export function detectSubdomain(): string | null {
  if (typeof window === 'undefined') {
    // Server-side rendering fallback
    return null;
  }

  // Priority 1: URL parameter (for development testing)
  const urlSubdomain = getSubdomainFromURLParam();
  if (urlSubdomain) {
    return urlSubdomain;
  }

  // Priority 2: Extract from hostname
  const hostname = window.location.hostname;
  const subdomain = extractSubdomain(hostname);

  return subdomain;
}

/**
 * Get configuration for current subdomain
 * @returns Configuration object with Supabase credentials and market info
 */
export function getSubdomainConfig(): SubdomainConfig {
  const subdomain = detectSubdomain();

  // Get market configuration based on subdomain
  const marketConfig = getMarketConfig(subdomain);

  // Read Supabase credentials from environment variables
  // These are the same variable names regardless of which country deployment
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

  // Validate configuration
  if (
    (!supabaseUrl || !supabaseAnonKey) && // In development, provide a helpful error message
    import.meta.env.DEV
  ) {
    throw new Error(
      `Missing Supabase configuration. Please add the following to your .env file:\n` +
        `VITE_SUPABASE_URL=https://your-project.supabase.co\n` +
        `VITE_SUPABASE_ANON_KEY=your-anon-key-here\n\n` +
        `For country-specific deployments, use the appropriate .env file:\n` +
        `- Korea: Copy .env.korea.example to .env\n` +
        `- Vietnam: Copy .env.vietnam.example to .env`
    );
  }

  return {
    supabaseUrl,
    supabaseAnonKey,
    market: marketConfig.market,
    defaultLanguage: marketConfig.defaultLanguage,
    subdomain: subdomain ?? 'default',
  };
}

/**
 * Get available subdomains
 * @returns List of configured subdomains
 */
export function getAvailableSubdomains(): string[] {
  return Object.keys(MARKET_CONFIG);
}

/**
 * Check if a subdomain is configured
 * @param subdomain - Subdomain to check
 * @returns True if subdomain is configured
 */
export function isSubdomainConfigured(subdomain: string): boolean {
  return subdomain in MARKET_CONFIG;
}

// Export configuration mapping for testing/debugging
export { MARKET_CONFIG };
